import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Crown, Sparkles, CheckCircle2, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function MemberDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [d, p, r] = await Promise.all([
        api.get("/member/dashboard"),
        api.get("/plans"),
        api.get("/member/reservations"),
      ]);
      setData(d.data);
      setPlans(p.data.plans);
      setReservations(r.data.reservations);
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const plan = params.get("plan");
    if (plan && plans.length) {
      const match = plans.find((p) => p.lookup_key === plan);
      if (match?.payment_link) window.location.href = match.payment_link;
    }
    // eslint-disable-next-line
  }, [plans]);

  const checkout = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  const active = data?.membership_status === "active";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <DashHeader user={user} logout={logout} navigate={navigate} />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <p className="text-xs tracking-luxe uppercase text-gold mb-4">Suite de Miembro</p>
        <h1 className="font-serif-display text-4xl md:text-5xl mb-12">Buenos días, {user?.name?.split(" ")[0]}</h1>

        <div
          data-testid="membership-status-card"
          className={`p-10 lg:p-12 mb-12 flex flex-col md:flex-row md:items-center justify-between gap-8 ${
            active ? "border-2 border-gold bg-black/40" : "border border-white/15"
          }`}
        >
          <div className="flex items-start gap-5">
            <Crown size={40} strokeWidth={1} className={active ? "text-gold" : "text-white/40"} />
            <div>
              <h2 className="font-serif-display text-2xl mb-1">
                {active ? "Concierge+ Activa" : "Ser Socio Inactivo"}
              </h2>
              <p className="text-white/55 font-light text-sm">
                {active
                  ? "Su concierge dedicado está a su disposición, 24/7. Solo tiene que escribirnos."
                  : "Active Concierge+ para desbloquear su concierge privado y el acceso completo."}
              </p>
            </div>
          </div>
          <span
            data-testid="membership-badge"
            className={`text-xs tracking-luxe-sm uppercase px-4 py-2 whitespace-nowrap ${
              active ? "bg-gold text-black" : "border border-white/25 text-white/60"
            }`}
          >
            {active ? "Activa" : "Inactiva"}
          </span>
        </div>

        {active ? (
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Sparkles, t: "Planificar una Escapada", d: "Escriba a su concierge con un destino y unas fechas." },
              { icon: CheckCircle2, t: "Acceso VIP", d: "Solicite entradas, mesas y acceso exclusivo." },
              { icon: Clock, t: "Soporte 24/7", d: "Resolución inmediata, esté donde esté en el mundo." },
            ].map((c) => (
              <div key={c.t} className="border border-white/10 p-8 hover:border-gold/40 transition-colors" data-testid={`member-action-${c.t}`}>
                <c.icon size={26} strokeWidth={1} className="text-gold mb-6" />
                <h3 className="font-serif-display text-xl mb-2">{c.t}</h3>
                <p className="text-white/50 font-light text-sm">{c.d}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6 mb-16" data-testid="member-plans">
            {plans.map((p) => (
              <div key={p.id} className={`p-10 ${p.interval === "month" ? "border-2 border-gold" : "border border-white/15"}`}>
                <h3 className="font-serif-display text-2xl mb-2">{p.name}</h3>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-serif-display text-4xl text-gold">{p.price.toLocaleString("es-ES")} €</span>
                  <span className="text-white/50 font-light">/ {p.interval === "month" ? "mes" : "año"}</span>
                </div>
                <button
                  onClick={() => { window.location.href = p.payment_link; }}
                  data-testid={`member-checkout-${p.lookup_key}`}
                  className={`w-full py-4 text-xs tracking-luxe-sm uppercase transition-colors disabled:opacity-50 ${
                    p.interval === "month" ? "bg-gold text-black hover:bg-[#C5A059]" : "border border-white/25 hover:border-gold hover:text-gold"
                  }`}
                >
                  Activar
                </button>
              </div>
            ))}
          </div>
        )}

        <h3 className="font-serif-display text-2xl mb-6">Mis Reservas</h3>
        <div className="border border-white/10 mb-16" data-testid="member-reservations">
          {reservations.length ? reservations.map((r) => (
            <div key={r.id} className="flex flex-wrap justify-between items-center gap-3 px-8 py-5 border-b border-white/10 last:border-0" data-testid={`member-res-${r.id}`}>
              <span className="font-light text-sm text-white/80">{r.service_type}{r.destination ? ` · ${r.destination}` : ""}</span>
              <span className="font-light text-sm text-white/50">{[r.start_date, r.end_date].filter(Boolean).join(" → ") || "—"}</span>
              <span className="font-light text-sm text-gold">{(r.value ?? 0).toLocaleString("es-ES")} €</span>
              <span className="text-xs tracking-luxe-sm uppercase text-white/60">{r.status}</span>
            </div>
          )) : (
            <p className="px-8 py-8 text-white/40 font-light text-sm">Aún no tiene reservas. Su concierge las gestionará por usted.</p>
          )}
        </div>

        <h3 className="font-serif-display text-2xl mb-6">Historial de Facturación</h3>
        <div className="border border-white/10" data-testid="billing-history">
          {data?.payments?.length ? (
            data.payments.map((p) => (
              <div key={p.id} className="flex justify-between items-center px-8 py-5 border-b border-white/10 last:border-0">
                <span className="font-light text-sm text-white/70">{p.lookup_key?.replace(/_/g, " ")}</span>
                <span className="font-light text-sm">{p.amount?.toLocaleString("es-ES")} {p.currency?.toUpperCase()}</span>
                <span className="text-xs tracking-luxe-sm uppercase text-gold">{p.payment_status === "paid" ? "pagado" : p.payment_status}</span>
              </div>
            ))
          ) : (
            <p className="px-8 py-8 text-white/40 font-light text-sm">Aún no hay transacciones.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function DashHeader({ user, logout, navigate }) {
  return (
    <header className="backdrop-blur-xl bg-black/60 border-b border-white/10 sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <span className="font-serif-display text-2xl cursor-pointer" onClick={() => navigate("/")}>
          Sol<span className="text-gold">Club</span>
        </span>
        <div className="flex items-center gap-6">
          <span className="text-white/50 font-light text-sm hidden md:inline">{user?.email}</span>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-2 text-xs tracking-luxe-sm uppercase text-white/70 hover:text-gold transition-colors"
          >
            <LogOut size={16} strokeWidth={1.5} /> Salir
          </button>
        </div>
      </div>
    </header>
  );
}
